/**
 * Single source of truth for the Shopify constants that have to be bumped together.
 *
 * Consumers:
 * - `app/shopify.server.ts`       — Admin API version the runtime client talks to
 * - `app/api/shopify/download.ts` — endpoint + output path of the schema snapshot
 * - `app/api/shopify/typegen.ts`  — schema snapshot the generated types are built from
 * - `.graphqlrc.ts`               — version used by the graphql-config preset (IDE)
 *
 * Bumping the Admin API version = change `SHOPIFY_ADMIN_API_VERSION` here, then run
 * `yarn schema --gx --download && yarn schema --gx --typegen`.
 */

/**
 * Admin API version the app talks to.
 * Must be a value of the `ApiVersion` enum shipped by the installed `@shopify/shopify-api`
 * (`app/shopify.server.ts` type-checks it against that enum).
 *
 * @see https://shopify.dev/docs/api/usage/versioning
 */
export const SHOPIFY_ADMIN_API_VERSION = '2026-07';

/** Local snapshot of the Admin API schema, written by `yarn schema --gx --download`. */
export const SHOPIFY_ADMIN_SCHEMA_PATH = `app/schemas/shopify/admin-${SHOPIFY_ADMIN_API_VERSION}.schema.json`;

/**
 * Handle of the cart transform Function, declared in
 * `extensions/cart-transformer/shopify.extension.toml` (`handle = "cart-transformer"`).
 *
 * Since 2025-10 `cartTransformCreate` takes `functionHandle` instead of the deprecated
 * `functionId`: the handle is app-scoped and stable across environments, while the id
 * changes on every deploy to a different environment.
 *
 * @see https://shopify.dev/changelog/introducing-functionhandle
 * @see https://shopify.dev/docs/api/admin-graphql/2026-07/mutations/cartTransformCreate
 */
export const SHOPIFY_CART_TRANSFORM_FUNCTION_HANDLE = 'cart-transformer';

/**
 * Handle of the app backing the Online Store channel — how its publication is told from the other
 * channels a resource may be published to. Stable across stores, unlike the publication's name,
 * which is the channel app's title and so is translated.
 */
export const SHOPIFY_ONLINE_STORE_APP_HANDLE = 'online_store';
